import React, { useState } from 'react';
import axios from '../config/axios';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#3b82f6'];

const defaultTemplate = {
    subject: 'Your DadBucks Weekly Report 📊',
    kidName: 'Alex',
    primaryColor: '#6366f1',
    footerText: 'Sent with ❤️ by DadBucks — Teaching kids the value of money.',
};

function generatePreviewHTML({ subject, kidName, primaryColor, footerText }) {
    return `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#f9fafb;margin:0;padding:20px;">
  <div style="max-width:600px;margin:0 auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">
    <div style="background:${primaryColor};padding:32px;text-align:center;color:#fff;">
      <h1 style="margin:0;font-size:28px;">📊 Weekly Progress Report</h1>
      <p style="margin:8px 0 0;opacity:0.9;">Preview for ${kidName}</p>
    </div>
    <div style="padding:24px;text-align:center;border-bottom:1px solid #e5e7eb;">
      <div style="font-size:48px;margin-bottom:8px;">🧒</div>
      <h2 style="margin:0;color:#1f2937;">${kidName}'s Week</h2>
    </div>
    <div style="padding:24px;display:grid;grid-template-columns:repeat(2,1fr);gap:16px;">
      <div style="background:#eff6ff;border-radius:12px;padding:20px;text-align:center;">
        <div style="font-size:32px;font-weight:bold;color:#2563eb;">7/10</div>
        <div style="color:#64748b;font-size:14px;">Chores Completed</div>
      </div>
      <div style="background:#f0fdf4;border-radius:12px;padding:20px;text-align:center;">
        <div style="font-size:32px;font-weight:bold;color:#16a34a;">$15.50</div>
        <div style="color:#64748b;font-size:14px;">Earned This Week</div>
      </div>
      <div style="background:#fef3c7;border-radius:12px;padding:20px;text-align:center;">
        <div style="font-size:32px;font-weight:bold;color:#d97706;">$42.00</div>
        <div style="color:#64748b;font-size:14px;">Current Balance</div>
      </div>
      <div style="background:#fce7f3;border-radius:12px;padding:20px;text-align:center;">
        <div style="font-size:32px;font-weight:bold;color:#db2777;">🔥 5</div>
        <div style="color:#64748b;font-size:14px;">Day Streak</div>
      </div>
    </div>
    <div style="background:#f9fafb;padding:24px;text-align:center;color:#6b7280;font-size:12px;">
      <p style="margin:0;">${footerText}</p>
    </div>
  </div>
</body>
</html>`;
}

/**
 * EmailTemplateEditor — Live HTML preview for email templates with a "Send Test Email" feature.
 */
const EmailTemplateEditor = () => {
    const [fields, setFields] = useState(defaultTemplate);
    const [sending, setSending] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [feedback, setFeedback] = useState(null);

    const previewHTML = generatePreviewHTML(fields);

    const handleChange = (key, value) => {
        setFields(f => ({ ...f, [key]: value }));
    };

    const handleSendTest = async () => {
        if (!testEmail.trim()) {
            setFeedback({ type: 'error', msg: 'Please enter an email address to send the test to.' });
            return;
        }
        setSending(true);
        setFeedback(null);
        try {
            await axios.post('/api/v1/email/preview', {
                to_email: testEmail,
                subject: fields.subject,
                primary_color: fields.primaryColor,
                footer_text: fields.footerText,
                kid_name: fields.kidName,
            });
            setFeedback({ type: 'success', msg: `✅ Test email sent to ${testEmail}` });
        } catch (err) {
            setFeedback({ type: 'error', msg: `❌ Failed to send: ${err.response?.data?.error || err.message}` });
        } finally {
            setSending(false);
        }
    };

    return (
        <div style={{ display: 'flex', gap: '24px', minHeight: '600px' }}>
            {/* Left: Controls */}
            <div style={{ width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <h3 style={{ fontSize: '1.1rem', color: '#1f2937', margin: 0 }}>✏️ Customize Template</h3>

                <div>
                    <label style={labelStyle}>Email Subject</label>
                    <input id="email-subject" style={inputStyle} value={fields.subject} onChange={e => handleChange('subject', e.target.value)} />
                </div>

                <div>
                    <label style={labelStyle}>Kid's Name (placeholder)</label>
                    <input id="email-kid-name" style={inputStyle} value={fields.kidName} onChange={e => handleChange('kidName', e.target.value)} />
                </div>

                <div>
                    <label style={labelStyle}>Primary Color</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {COLORS.map(color => (
                            <button
                                key={color}
                                onClick={() => handleChange('primaryColor', color)}
                                style={{
                                    width: '32px', height: '32px',
                                    borderRadius: '8px',
                                    background: color,
                                    border: fields.primaryColor === color ? '3px solid #1f2937' : '2px solid transparent',
                                    cursor: 'pointer',
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>Footer Text</label>
                    <textarea id="email-footer" style={{ ...inputStyle, height: '72px', resize: 'vertical' }} value={fields.footerText} onChange={e => handleChange('footerText', e.target.value)} />
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb' }} />

                <div>
                    <label style={labelStyle}>Send Test Email To</label>
                    <input id="email-test-address" type="email" style={inputStyle} placeholder="you@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} />
                </div>

                <button
                    id="send-test-email-btn"
                    onClick={handleSendTest}
                    disabled={sending}
                    style={{ padding: '12px', borderRadius: '12px', border: 'none', background: '#6366f1', color: '#fff', fontWeight: '700', cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.7 : 1 }}
                >
                    {sending ? 'Sending…' : '📤 Send Test Email'}
                </button>

                {feedback && (
                    <div style={{
                        padding: '12px',
                        borderRadius: '10px',
                        background: feedback.type === 'success' ? '#d1fae5' : '#fee2e2',
                        color: feedback.type === 'success' ? '#065f46' : '#991b1b',
                        fontSize: '13px',
                        fontWeight: '600',
                    }}>
                        {feedback.msg}
                    </div>
                )}
            </div>

            {/* Right: Live Preview */}
            <div style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden' }}>
                <div style={{ background: '#f3f4f6', padding: '10px 16px', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                    📧 Live Preview — <strong>{fields.subject}</strong>
                </div>
                <iframe
                    id="email-preview-iframe"
                    title="Email Preview"
                    srcDoc={previewHTML}
                    style={{ width: '100%', height: '560px', border: 'none' }}
                    sandbox="allow-same-origin"
                />
            </div>
        </div>
    );
};

const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
};

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    boxSizing: 'border-box',
    color: '#1f2937',
    outline: 'none',
};

export default EmailTemplateEditor;
